import React from 'react'
import { useDispatch } from 'react-redux'
import { Fit } from 'types/fit'
import { customerSetFitAction } from '@store/redux/actions/customerFitActions'
import { analytics } from '@helpers/analytics'
import Link from '@components/ui/Link'
import { Button, SubmitButton } from '@components/ui/Button'
import Modal from '@components/ui/Modal'
import { SubmitButtonHandlers } from '@components/ui/Button/SubmitButton'
import { UIState } from '../types'
import SaveFitWrapper from '../SavedFitWrapper'
import styles from '../shared/savedFitModalContainer.module.scss'
import { updateCustomerFit } from '../helpers'

export const FIT_UPDATE_EVENT = 'Fit Update Prompt'

type SavedFitConfirmationModalProperties = {
  title: string
  description: string
  method: string
  category: string
  savedFit: Fit
  showFitFinderLink?: boolean
  onClose?: () => void
}

export default function SavedFitConfirmationModal({
  savedFit,
  title,
  description,
  category,
  method,
  showFitFinderLink = true,
  onClose = () => undefined
}: SavedFitConfirmationModalProperties) {
  const dispatch = useDispatch()

  const handleKeepCurrentFit = async ({
    triggerButtonError,
    triggerButtonSuccess
  }: SubmitButtonHandlers) => {
    try {
      await updateCustomerFit(savedFit, method)

      analytics(FIT_UPDATE_EVENT, {
        action: 'Fit confirmed',
        category
      })

      await triggerButtonSuccess()

      dispatch(
        customerSetFitAction({
          updatedAt: new Date().toISOString(),
          fit: savedFit
        })
      )

      onClose()
    } catch (error) {
      triggerButtonError()
    }
  }

  const handleClose = () => {
    dispatch(
      customerSetFitAction({
        updatedAt: new Date().toISOString()
      })
    )
    analytics(FIT_UPDATE_EVENT, {
      action: 'Modal closed',
      category
    })
    onClose()
  }

  const handleAfterSubmit = () => {
    analytics(FIT_UPDATE_EVENT, {
      action: 'Fit edited',
      category
    })
    onClose()
  }

  const handleEditFit = (setUIState: (uiState: UIState) => void) => {
    setUIState('edit')
    analytics('Click', {
      label: 'Edit my results',
      category
    })
  }

  return (
    <Modal onClose={handleClose} openedModal={true}>
      <div className={styles.savedFitModalContainer}>
        <div className={styles.savedFitModalWrapper}>
          <SaveFitWrapper
            method={method}
            title={title}
            description={description}
            savedFit={savedFit}
            showImage={false}
            afterSubmit={handleAfterSubmit}
            showShopButtons={false}
          >
            {({ uiState, setUIState }) => {
              return (
                <>
                  {uiState === 'read' && (
                    <div
                      className={styles.savedFitModalContainer__buttonWrapper}
                    >
                      <Button
                        variant="secondary"
                        onClick={() => handleEditFit(setUIState)}
                      >
                        Edit
                      </Button>
                      <SubmitButton
                        variant="primary"
                        onClick={handleKeepCurrentFit}
                      >
                        My Fit is correct
                      </SubmitButton>
                    </div>
                  )}
                </>
              )
            }}
          </SaveFitWrapper>
        </div>
        {!!showFitFinderLink && (
          <div className={styles.savedFitModalContainer__Footer}>
            <h5 className={styles.savedFitModalContainer__Footer_title}>
              Not sure what your fit is?
            </h5>
            <Link
              onClick={() => {
                analytics(FIT_UPDATE_EVENT, {
                  action: 'Fit Finder',
                  category
                })
              }}
              text="Retake the Fit Finder"
              href="/pages/fit-finder"
            />
          </div>
        )}
      </div>
    </Modal>
  )
}
